import React, {useMemo} from 'react';
import {get} from 'lodash';

import CheckboxField from 'components/CheckboxField';
import Typography from 'components/Typography';

import {Order, OrderType} from 'types';

import styles from './Ticket.module.scss';
import classNames from 'classnames';
import {EditIcon} from './edit';
import {PinIcon} from './pin';
import PriorityFlag from 'components/PriorityFlag';
import StatusBadge from 'components/StatusBadge';
import {getFullName} from 'helpers/user';
import ChainIcon from '../../icons/chain';

interface Props extends Order {
  index?: number;
  selected?: boolean;
  timePreference?: string;
  borough?: string;
  onChangeCheckbox: (id: number, checked: boolean) => void;
  handleNoteClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  showTime?: boolean;
  isPrint?: boolean;
  isError?: boolean;
}

function naming(arrival_time?: string) {
  if (typeof arrival_time === 'string') {
    const [hour, minute] = arrival_time.split(':');
    let hourInt = parseInt(hour, 10);
    let minuteInt = parseInt(minute, 10);

    let period = 'AM';
    if (hourInt >= 12) {
      period = 'PM';
    }

    if (hourInt === 0) {
      hourInt = 12;
    } else if (hourInt > 12) {
      hourInt -= 12;
    }
    return `${hourInt}:${minuteInt.toString().padStart(2, '0')} ${period}`;
  }
  return '';
}
function Ticket({
  index,
  id,
  delivery_address,
  arrival_time,
  selected,
  customer,
  onChangeCheckbox,
  handleNoteClick,
  showTime = false,
  isPrint = false,
  isError,
  orders,
  isDragging,
  sequential_order_number,
  order_type,
}: Props) {
  const priorities = useMemo(
    () =>
      orders
        ?.filter((order) => !!order.priority)
        .map((order) => order.priority),
    [orders]
  );
  const isVendor = customer?.client_role === 'vendors';
  const pickup_number = orders?.[0]?.pickup;
  const hasPickup = order_type === 'dropoff' && pickup_number !== null;
  const customerName = isVendor
    ? customer?.customer_company
    : getFullName(customer?.user);

  const flag_reason = orders?.[0]?.flag_reason;
  const renderOrderType = (order_type?: OrderType) => {
    if (order_type === 'pickup') {
      return (
        <StatusBadge color="default" className={styles.orderTypeBadge}>
          P{sequential_order_number}
        </StatusBadge>
      );
    }
    if (order_type === 'dropoff') {
      return (
        <StatusBadge color="green" className={styles.orderTypeBadge}>
          D{sequential_order_number}
        </StatusBadge>
      );
    }
    return null;
  };
  return (
    <div
      className={classNames(
        styles.root,
        ((typeof delivery_address !== 'string' &&
          !delivery_address?.is_valid) ||
          isError) &&
          styles.borderColor,
        isDragging && styles.isDragging
      )}
    >
      {isPrint ? (
        <div className={styles.top}>
          <Typography variant="h4">STOP # {index ? index : ''}</Typography>
          {typeof arrival_time === 'string' && showTime && (
            <span className={styles.delivery_time}>
              {' '}
              {'-  '}
              {naming(arrival_time)}
            </span>
          )}
        </div>
      ) : (
        <div
          style={{
            marginBottom: !isPrint ? 0 : 8,
          }}
          className={styles.top}
        >
          <CheckboxField
            name={`checkbox-${id}`}
            className={styles.checkbox}
            checked={selected}
            onChange={() => onChangeCheckbox(id, !selected)}
          />
        </div>
      )}

      <div className={styles.content}>
        <div className={styles.contentTop}>
          {typeof customer === 'string' ? (
            <Typography className={styles.customer} variant="h4">
              {customer}
            </Typography>
          ) : (
            <Typography className={styles.customer} variant="h4">
              {customerName}
              {renderOrderType(order_type)}
            </Typography>
          )}
          {!!priorities?.length && (
            <PriorityFlag
              priority={priorities[0] as any}
              flag_reason={flag_reason as string | null}
            />
          )}
        </div>

        <Typography className={styles.items} variant="strong">
          {orders
            ?.map((order) =>
              order?.items?.map((item) => get(item, 'drug.name', ''))
            )
            ?.join(', ')}
        </Typography>

        {hasPickup && (
          <div className={styles.addressContainer}>
            <ChainIcon className={styles.pinIcon} />
            <Typography
              className={styles.addressCalc}
              variant="p"
              style={{marginBottom: 0}}
            >
              Associated Pickup: #{pickup_number}
            </Typography>
          </div>
        )}
        {typeof delivery_address === 'string' ? (
          <div className={styles.addressContainer}>
            <PinIcon className={styles.pinIcon} />
            <Typography className={styles.addressCalc} variant="p">
              {delivery_address}
            </Typography>
          </div>
        ) : (
          <div className={styles.addressContainer}>
            <PinIcon className={styles.pinIcon} />
            <Typography className={styles.address} variant="p">
              {delivery_address?.display_address}
            </Typography>
          </div>
        )}
        <div className={styles.footer}>
          <div onClick={handleNoteClick}>
            <EditIcon className={styles.editIcon} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default Ticket;
